import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiClient } from '../../lib/api-client';

// Cache duration in milliseconds (3 hours)
const CACHE_DURATION = 3 * 60 * 60 * 1000;

// Types
export interface QuickLink {
  _id: string;
  title: string;
  url: string;
  order: number;
  isActive: boolean;
}

export interface SupportLink {
  _id: string;
  title: string;
  url: string;
  order: number;
  isActive: boolean;
}

export interface SocialLink {
  _id: string;
  platform: 'facebook' | 'twitter' | 'instagram' | 'linkedin' | 'youtube' | 'pinterest';
  url: string;
  isActive: boolean;
}

export interface ContactInfo {
  address: string;
  phone: string;
  email: string;
}

export interface Branding {
  name: string;
  logo: string;
  description: string;
  tagline: string;
}

export interface FeatureSettings {
  discoverTabEnabled: boolean;
  liveTabEnabled: boolean;
  activityTabEnabled: boolean;
  messagesTabEnabled: boolean;
  plansTabEnabled: boolean;
}

export interface AutoInterestSettings {
  enabled: boolean;
  sourceType: 'dummy' | 'user';
}

export interface AppBannerSettings {
  enabled: boolean;
  showPopup: boolean;
  targetDevice?: 'all' | 'mobile_only' | 'desktop_only';
  showFrequency?: 'once_per_day' | 'once_per_session' | 'always';
  text: string;
  popupTitle?: string;
  popupText?: string;
  buttonText: string;
  playStoreUrl: string;
  badgeText: string;
}

export interface Settings {
  _id: string;
  branding: Branding;
  contactInfo: ContactInfo;
  quickLinks: QuickLink[];
  supportLinks: SupportLink[];
  socialLinks: SocialLink[];
  features?: FeatureSettings;
  autoInterestSettings?: AutoInterestSettings;
  appBanner?: AppBannerSettings;
  createdAt: string;
  updatedAt: string;
}

interface SettingsState {
  settings: Settings | null;
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
}

// Local storage helpers
const STORAGE_KEY = 'app_settings';
const TIMESTAMP_KEY = 'app_settings_timestamp';

const saveToLocalStorage = (settings: Settings) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    localStorage.setItem(TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('Failed to save settings to localStorage:', error);
  }
};

const loadFromLocalStorage = (): { settings: Settings | null; timestamp: number | null } => {
  try {
    const settingsData = localStorage.getItem(STORAGE_KEY);
    const timestamp = localStorage.getItem(TIMESTAMP_KEY);

    if (settingsData && timestamp) {
      const parsedTimestamp = parseInt(timestamp, 10);
      const now = Date.now();

      // Check if cache is still valid
      if (now - parsedTimestamp < CACHE_DURATION) {
        return {
          settings: JSON.parse(settingsData),
          timestamp: parsedTimestamp,
        };
      }
    }
  } catch (error) {
    console.error('Failed to load settings from localStorage:', error);
  }

  return { settings: null, timestamp: null };
};

// Initial state
const { settings: cachedSettings, timestamp } = loadFromLocalStorage();

const initialState: SettingsState = {
  settings: cachedSettings || null, // Don't use default settings
  loading: false,
  error: null,
  lastFetched: timestamp,
};

// Async thunks
export const fetchSettings = createAsyncThunk(
  'settings/fetchSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.getSettings();
      return response.settings;
    } catch (error: any) {
      // Silently fail - just log to console without showing warnings
      // This allows the app to work without backend in development mode
      console.log('Settings not loaded from backend - using cached/default values');
      // Return null to indicate backend is unavailable
      // Footer will show minimal version without admin-configured data
      throw error;
    }
  }
);

export const updateBranding = createAsyncThunk(
  'settings/updateBranding',
  async (branding: Partial<Branding>, { rejectWithValue }) => {
    try {
      const response = await apiClient.updateBranding(branding);
      return response.branding;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update branding');
    }
  }
);

export const updateContactInfo = createAsyncThunk(
  'settings/updateContactInfo',
  async (contactInfo: Partial<ContactInfo>, { rejectWithValue }) => {
    try {
      const response = await apiClient.updateContactInfo(contactInfo);
      return response.contactInfo;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update contact info');
    }
  }
);

export const addQuickLink = createAsyncThunk(
  'settings/addQuickLink',
  async (link: { title: string; url: string; order?: number }, { rejectWithValue }) => {
    try {
      const response = await apiClient.addQuickLink(link);
      return response.quickLinks;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add quick link');
    }
  }
);

export const updateQuickLink = createAsyncThunk(
  'settings/updateQuickLink',
  async ({ linkId, data }: { linkId: string; data: Partial<QuickLink> }, { rejectWithValue }) => {
    try {
      const response = await apiClient.updateQuickLink(linkId, data);
      return response.quickLinks;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update quick link');
    }
  }
);

export const deleteQuickLink = createAsyncThunk(
  'settings/deleteQuickLink',
  async (linkId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.deleteQuickLink(linkId);
      return response.quickLinks;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete quick link');
    }
  }
);

export const addSupportLink = createAsyncThunk(
  'settings/addSupportLink',
  async (link: { title: string; url: string; order?: number }, { rejectWithValue }) => {
    try {
      const response = await apiClient.addSupportLink(link);
      return response.supportLinks;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add support link');
    }
  }
);

export const updateSupportLink = createAsyncThunk(
  'settings/updateSupportLink',
  async ({ linkId, data }: { linkId: string; data: Partial<SupportLink> }, { rejectWithValue }) => {
    try {
      const response = await apiClient.updateSupportLink(linkId, data);
      return response.supportLinks;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update support link');
    }
  }
);

export const deleteSupportLink = createAsyncThunk(
  'settings/deleteSupportLink',
  async (linkId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.deleteSupportLink(linkId);
      return response.supportLinks;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete support link');
    }
  }
);

export const addSocialLink = createAsyncThunk(
  'settings/addSocialLink',
  async (link: { platform: string; url: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.addSocialLink(link);
      return response.socialLinks;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add social link');
    }
  }
);

export const updateSocialLink = createAsyncThunk(
  'settings/updateSocialLink',
  async ({ linkId, data }: { linkId: string; data: Partial<SocialLink> }, { rejectWithValue }) => {
    try {
      const response = await apiClient.updateSocialLink(linkId, data);
      return response.socialLinks;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update social link');
    }
  }
);

export const deleteSocialLink = createAsyncThunk(
  'settings/deleteSocialLink',
  async (linkId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.deleteSocialLink(linkId);
      return response.socialLinks;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete social link');
    }
  }
);

// 🔥 NEW: Update feature settings
export const updateFeatureSettings = createAsyncThunk(
  'settings/updateFeatureSettings',
  async (data: Partial<FeatureSettings>, { rejectWithValue }) => {
    try {
      const response = await apiClient.updateFeatureSettings(data);
      return response.features;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update feature settings');
    }
  }
);

// 🔥 NEW: Update auto-interest settings
export const updateAutoInterestSettings = createAsyncThunk(
  'settings/updateAutoInterestSettings',
  async (data: Partial<AutoInterestSettings>, { rejectWithValue }) => {
    try {
      const response = await apiClient.updateAutoInterestSettings(data);
      return response.autoInterestSettings;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update auto-interest settings');
    }
  }
);

// 🔥 NEW: Update app banner settings
export const updateAppBannerSettings = createAsyncThunk(
  'settings/updateAppBannerSettings',
  async (data: Partial<AppBannerSettings>, { rejectWithValue }) => {
    try {
      const response = await apiClient.updateAppBannerSettings(data);
      return response.appBanner;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update app banner settings');
    }
  }
);

// Slice
const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearSettingsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch settings
    builder
      .addCase(fetchSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSettings.fulfilled, (state, action: PayloadAction<Settings>) => {
        state.loading = false;
        state.settings = action.payload;
        state.lastFetched = Date.now();
        saveToLocalStorage(action.payload);
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Keep using cached/default settings
      });

    // Update branding
    builder
      .addCase(updateBranding.fulfilled, (state, action: PayloadAction<Branding>) => {
        if (state.settings) {
          state.settings.branding = action.payload;
          saveToLocalStorage(state.settings);
        }
      });

    // Update contact info
    builder
      .addCase(updateContactInfo.fulfilled, (state, action: PayloadAction<ContactInfo>) => {
        if (state.settings) {
          state.settings.contactInfo = action.payload;
          saveToLocalStorage(state.settings);
        }
      });

    // Quick links
    builder
      .addCase(addQuickLink.fulfilled, (state, action: PayloadAction<QuickLink[]>) => {
        if (state.settings) {
          state.settings.quickLinks = action.payload;
          saveToLocalStorage(state.settings);
        }
      })
      .addCase(updateQuickLink.fulfilled, (state, action: PayloadAction<QuickLink[]>) => {
        if (state.settings) {
          state.settings.quickLinks = action.payload;
          saveToLocalStorage(state.settings);
        }
      })
      .addCase(deleteQuickLink.fulfilled, (state, action: PayloadAction<QuickLink[]>) => {
        if (state.settings) {
          state.settings.quickLinks = action.payload;
          saveToLocalStorage(state.settings);
        }
      });

    // Support links
    builder
      .addCase(addSupportLink.fulfilled, (state, action: PayloadAction<SupportLink[]>) => {
        if (state.settings) {
          state.settings.supportLinks = action.payload;
          saveToLocalStorage(state.settings);
        }
      })
      .addCase(updateSupportLink.fulfilled, (state, action: PayloadAction<SupportLink[]>) => {
        if (state.settings) {
          state.settings.supportLinks = action.payload;
          saveToLocalStorage(state.settings);
        }
      })
      .addCase(deleteSupportLink.fulfilled, (state, action: PayloadAction<SupportLink[]>) => {
        if (state.settings) {
          state.settings.supportLinks = action.payload;
          saveToLocalStorage(state.settings);
        }
      });

    // Social links
    builder
      .addCase(addSocialLink.fulfilled, (state, action: PayloadAction<SocialLink[]>) => {
        if (state.settings) {
          state.settings.socialLinks = action.payload;
          saveToLocalStorage(state.settings);
        }
      })
      .addCase(updateSocialLink.fulfilled, (state, action: PayloadAction<SocialLink[]>) => {
        if (state.settings) {
          state.settings.socialLinks = action.payload;
          saveToLocalStorage(state.settings);
        }
      })
      .addCase(deleteSocialLink.fulfilled, (state, action: PayloadAction<SocialLink[]>) => {
        if (state.settings) {
          state.settings.socialLinks = action.payload;
          saveToLocalStorage(state.settings);
        }
      });

    // 🔥 NEW: Feature settings
    builder
      .addCase(updateFeatureSettings.fulfilled, (state, action: PayloadAction<FeatureSettings>) => {
        if (state.settings) {
          state.settings.features = action.payload;
          saveToLocalStorage(state.settings);
        }
      });

    // 🔥 NEW: Auto-interest settings
    builder
      .addCase(updateAutoInterestSettings.fulfilled, (state, action: PayloadAction<AutoInterestSettings>) => {
        if (state.settings) {
          state.settings.autoInterestSettings = action.payload;
          saveToLocalStorage(state.settings);
        }
      });

    // 🔥 NEW: App banner settings
    builder
      .addCase(updateAppBannerSettings.fulfilled, (state, action: PayloadAction<AppBannerSettings>) => {
        if (state.settings) {
          state.settings.appBanner = action.payload;
          saveToLocalStorage(state.settings);
        }
      });
  },
});

export const { clearSettingsError } = settingsSlice.actions;
export default settingsSlice.reducer;